/**
 * The General Knowledge upload dialog (per the approved design canvas): pick
 * how the file is used — a curated rule (extract, review, publish) or a
 * reference file (stored and indexed as uploaded) — then drop the file.
 * Nothing persists on the rule path until the user saves the draft; the
 * reference path stores immediately.
 */
import { useRef, useState } from 'react';
import { Banner, Button, Modal, Select } from '@servicecompressionllc/sc-design-system';
import { Upload } from '@servicecompressionllc/sc-design-system/icons';
import { ACCEPTED } from './SourceDocuments.js';
import { type KnowledgeBucket, type KnowledgeSubject } from './api.js';
import { SubjectPickerModal } from './SubjectPickerModal.js';
import { humanSize } from '../../lib/format.js';

export type GkUploadKind = 'rule' | 'reference';

const ChoiceCard = ({
  selected,
  title,
  description,
  onSelect,
}: {
  selected: boolean;
  title: string;
  description: string;
  onSelect: () => void;
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={`flex flex-col gap-1 rounded-md border p-3 text-left ${
      selected ? 'border-primary' : 'border-outline hover:bg-surface-hover'
    } bg-surface`}
  >
    <span className="text-sm font-semibold text-ink">{title}</span>
    <span className="text-xs leading-relaxed text-ink-muted">{description}</span>
  </button>
);

export const AddToGkModal = ({
  open,
  busy,
  error,
  bucket,
  buckets,
  subjects = [],
  onClose,
  onSubmit,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  /** Fixed destination — set when opened FROM a bucket page (no picker). */
  bucket?: KnowledgeBucket;
  /** Choosable destinations — set when opened from the overview. */
  buckets?: KnowledgeBucket[];
  /** The equipment a reference file can be tagged with. */
  subjects?: KnowledgeSubject[];
  onClose: () => void;
  /** Called with the chosen file, shelf, bucket and equipment ([] = everywhere); the caller owns the mutation. */
  onSubmit: (
    file: File,
    kind: GkUploadKind,
    bucketId: string | undefined,
    subjectIds: string[],
  ) => void;
}) => {
  const [kind, setKind] = useState<GkUploadKind>('rule');
  const [file, setFile] = useState<File | null>(null);
  const [pickedId, setPickedId] = useState<string | undefined>(undefined);
  // A reference file's reach is a deliberate choice: neither option starts selected.
  const [appliesTo, setAppliesTo] = useState<'equipment' | 'everywhere' | null>(null);
  const [equipment, setEquipment] = useState<string[]>([]);
  const [picking, setPicking] = useState(false);
  const equipmentNames = equipment.map((id) => subjects.find((s) => s.id === id)?.name ?? id);
  const appliesToReady =
    kind === 'rule' ||
    appliesTo === 'everywhere' ||
    (appliesTo === 'equipment' && equipment.length > 0);
  const inputRef = useRef<HTMLInputElement>(null);
  const destination =
    bucket ??
    (buckets ?? []).find((b) => b.id === pickedId) ??
    (buckets ?? []).find((b) => b.isDefault);
  const boundGroups = (destination?.readGroups ?? []).map((g) => g.name).join(', ');

  const close = () => {
    setFile(null);
    setKind('rule');
    setPickedId(undefined);
    setAppliesTo(null);
    setEquipment([]);
    onClose();
  };

  return (
    <Modal open={open} onOpenChange={(o) => !o && close()}>
      <Modal.Header>
        <Modal.Title>Add to General Knowledge</Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
              Adding to
            </span>
            {bucket !== undefined ? (
              // Opened from a bucket page: the destination is where you are.
              <p className="text-sm text-ink">{bucket.name}</p>
            ) : (
              <Select
                value={[destination?.id ?? '']}
                onChange={(v) => setPickedId(v[0])}
                options={(buckets ?? []).map((b) => ({ value: b.id, label: b.name }))}
                aria-label="Bucket"
                searchable={false}
              />
            )}
            <span className="text-xs text-ink-subtle">
              {boundGroups.length > 0
                ? `Readable by members of ${boundGroups}.`
                : 'Readable by everyone who can search the knowledge base.'}
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
              How it is used
            </span>
            <div role="radiogroup" aria-label="How it is used" className="grid grid-cols-2 gap-2.5">
              <ChoiceCard
                selected={kind === 'rule'}
                title="Curated rule"
                description="Extracted into text you edit and publish; the file is discarded. Best for policies, FAQs, checklists."
                onSelect={() => setKind('rule')}
              />
              <ChoiceCard
                selected={kind === 'reference'}
                title="Reference file"
                description="Kept as uploaded and quoted by filename. Best for manuals and spec sheets."
                onSelect={() => setKind('reference')}
              />
            </div>
          </div>
          {kind === 'reference' && (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                Applies to
              </span>
              <div role="radiogroup" aria-label="Applies to" className="grid grid-cols-2 gap-2.5">
                <ChoiceCard
                  selected={appliesTo === 'equipment'}
                  title="Specific equipment"
                  description="Used only on questions about the equipment you pick. Best for manuals."
                  onSelect={() => {
                    setAppliesTo('equipment');
                    if (equipment.length === 0) setPicking(true);
                  }}
                />
                <ChoiceCard
                  selected={appliesTo === 'everywhere'}
                  title="Everywhere"
                  description="Used on every question. Best for policies and standards that hold for all equipment."
                  onSelect={() => setAppliesTo('everywhere')}
                />
              </div>
              {appliesTo === 'equipment' && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-outline-strong bg-surface p-3">
                  {equipmentNames.length === 0 ? (
                    <span className="text-sm text-ink-subtle">No equipment chosen yet.</span>
                  ) : (
                    equipmentNames.map((name) => (
                      <span
                        key={name}
                        className="rounded-md border border-outline-strong bg-surface-elevated px-2.5 py-1.5 text-sm font-medium text-ink"
                      >
                        {name}
                      </span>
                    ))
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    className="ml-auto"
                    onClick={() => setPicking(true)}
                  >
                    Choose equipment
                  </Button>
                </div>
              )}
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            aria-label="Choose a file to add"
            onChange={(e) => {
              const chosen = e.target.files?.[0];
              e.target.value = '';
              if (chosen) setFile(chosen);
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const dropped = e.dataTransfer.files[0];
              if (dropped) setFile(dropped);
            }}
            className="flex flex-col items-center gap-1.5 rounded-md border border-dashed border-outline-strong bg-surface p-6"
          >
            <Upload size={20} aria-hidden className="text-ink-subtle" />
            {file ? (
              <span className="text-sm text-ink">
                {file.name} <span className="text-ink-subtle">({humanSize(file.size)})</span>
              </span>
            ) : (
              <span className="text-sm text-ink">
                Drop a file here, or <span className="text-link">browse</span>
              </span>
            )}
            <span className="text-xs text-ink-subtle">
              PDF, DOCX, XLSX, TXT, MD, or CSV, up to 50 MB
            </span>
          </button>

          {error !== null && (
            <Banner variant="danger">
              <Banner.Description>{error}</Banner.Description>
            </Banner>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button
          disabled={file === null || busy || !appliesToReady}
          onClick={() =>
            file &&
            onSubmit(
              file,
              kind,
              destination && !destination.isDefault ? destination.id : undefined,
              kind === 'reference' && appliesTo === 'equipment' ? equipment : [],
            )
          }
        >
          {busy ? (kind === 'rule' ? 'Extracting…' : 'Uploading…') : 'Continue'}
        </Button>
      </Modal.Footer>
      <SubjectPickerModal
        open={picking}
        subjects={subjects}
        selected={equipment}
        onChange={setEquipment}
        onClose={() => setPicking(false)}
      />
    </Modal>
  );
};
